import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { useUpdateSetting } from '@/hooks/useSetting';
import { ScanSchedule, WeekDay } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';
import { useMutation } from '@/utils/api';
import { convertUTCToTimezone, padTime } from '@/utils/date.util';
import { appStorage } from '@/utils/storage/appStorage.util';
import { StorageKey } from '@/utils/storage/useStorage.util';

interface ScanScheduleProps {
  days: WeekDay[];
  start: string; // HH:MM 24h format
  end: string; // HH:MM 24h format
  manualBypass?: boolean;
}

/**
 * Helper function to convert time string to backend format
 * @param timeString Format: "HH:MM" (e.g., "09:00")
 * @returns Object with { hour: number, minute: number }
 */
const parseTime = (timeString: string): { hour: number; minute: number } => {
  const [hour, minute] = timeString.split(':').map(Number);
  return { hour, minute };
};

export function useScanSchedule({ email }: { email?: string } = {}) {
  const axios = useAxios({ email }); // Fix: Pass email parameter for impersonation support
  const { mutateAsync: updateSetting, isPending: isUpdating } =
    useUpdateSetting(email);

  const {
    data: settings,
    isPending: isLoadingScanSchedule,
    status,
    refetch: refetchScanSchedule,
  } = useMy(
    { resource: 'setting' },
    {
      email,
      staleTime: 30000, // 30 seconds
      gcTime: 300000, // 5 minutes
    }
  );

  const currentScanSchedule = settings?.find(s => s.name === 'schedule')
    ?.value as ScanSchedule | undefined;

  const savedTimezone = appStorage.getItem(
    StorageKey.SCAN_SCHEDULE_TIMEZONE
  ) as DropdownOption;

  // Set default schedule if none exists
  let days = currentScanSchedule?.days ?? [];
  let startTime = currentScanSchedule?.start
    ? `${padTime(currentScanSchedule?.start.hour)}:${padTime(currentScanSchedule?.start.minute)}`
    : undefined;
  let endTime = currentScanSchedule?.end
    ? `${padTime(currentScanSchedule?.end.hour)}:${padTime(currentScanSchedule?.end.minute)}`
    : undefined;

  // Convert to selected timezone if available
  if (savedTimezone?.value && currentScanSchedule?.start) {
    const utcStartTime = `${padTime(currentScanSchedule.start.hour)}:${padTime(currentScanSchedule.start.minute)}`;
    const convertedStart = convertUTCToTimezone(
      utcStartTime,
      savedTimezone.value as string
    );
    startTime = convertedStart.localTime;
  }

  if (savedTimezone?.value && currentScanSchedule?.end) {
    const utcEndTime = `${padTime(currentScanSchedule.end.hour)}:${padTime(currentScanSchedule.end.minute)}`;
    const convertedEnd = convertUTCToTimezone(
      utcEndTime,
      savedTimezone.value as string
    );
    endTime = convertedEnd.localTime;
  }

  // Map days to full day names
  const daysName = days.map(
    day =>
      (
        ({
          [WeekDay.Monday]: 'Monday',
          [WeekDay.Tuesday]: 'Tuesday',
          [WeekDay.Wednesday]: 'Wednesday',
          [WeekDay.Thursday]: 'Thursday',
          [WeekDay.Friday]: 'Friday',
          [WeekDay.Saturday]: 'Saturday',
          [WeekDay.Sunday]: 'Sunday',
        }) as Record<WeekDay, string>
      )[day]
  );

  const updateScanSchedule = useMutation<void, Error, ScanScheduleProps>({
    defaultErrorMessage: 'Failed to update scan schedule',
    mutationFn: async ({ days, start, end, manualBypass }) => {
      const promise = updateSetting({
        name: 'schedule',
        value: {
          days,
          start: parseTime(start),
          end: parseTime(end),
          manualBypass,
        },
        silent: true,
      });

      toast.promise(promise, {
        loading: 'Updating scan schedule',
        success: 'Scan schedule updated successfully',
        error: 'Failed to update scan schedule',
      });

      await promise;
    },
    onSuccess: () => {
      refetchScanSchedule();
    },
  });

  const deleteScanSchedule = useMutation<void, Error>({
    defaultErrorMessage: 'Failed to restore scan schedule to default',
    mutationFn: async () => {
      const promise = axios.delete('/setting', {
        data: { name: 'schedule' },
      });

      toast.promise(promise, {
        loading: 'Restoring scan schedule to default',
        success: 'Scan schedule restored to default setting',
        error: 'Failed to restore scan schedule to default',
      });

      await promise;
    },
    onSuccess: () => {
      refetchScanSchedule();
    },
  });

  return {
    currentScanSchedule: {
      days,
      daysName,
      start: startTime,
      end: endTime,
      manualBypass: currentScanSchedule?.manualBypass,
    },
    isLoadingScanSchedule,
    status,
    updateScanSchedule: updateScanSchedule.mutate,
    updateScanScheduleAsync: updateScanSchedule.mutateAsync,
    isUpdatingScanSchedule: isUpdating || updateScanSchedule.isPending,
    deleteScanSchedule: deleteScanSchedule.mutate,
    refetchScanSchedule,
  };
}
